import React, { CSSProperties } from 'react';

const SIZER_STYLES = {
  position: 'absolute',
  width: 0,
  height: 0,
  visibility: 'hidden',
  overflow: 'scroll',
  whiteSpace: 'pre',
};

const STYLE_PROPS = ['fontSize', 'fontFamily', 'fontWeight', 'fontStyle', 'letterSpacing', 'textTransform'];

interface Props {
  query: string;
  placeholderText: string;
  autoresize?: boolean;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  ariaLabelText?: string;
  id: string;
  expanded?: boolean;
  isFocused?: boolean;
  classNames?: any;
  inputAttributes?: any;
  inputEventHandlers?: any;
  index?: number;
  inputType?: string;
  rightInnerComponent?: React.ReactNode;
}

interface State {
  inputWidth: number;
  isFocused: boolean;
}

class Input extends React.Component<Props, State> {
  private input: React.RefObject<HTMLInputElement>;
  private sizer: React.RefObject<HTMLDivElement>;
  private focusTimeoutId;

  constructor(props) {
    super(props);
    this.state = { inputWidth: null, isFocused: null };

    this.input = React.createRef();
    this.sizer = React.createRef();
  }

  componentDidMount() {
    if (this.props.autoresize) {
      this.copyInputStyles();
      this.updateInputWidth();
    }

    this.focusTimeoutId = setTimeout(() => {
      if (this.props.isFocused) {
        this.input.current.focus();
      }
    }, 0);
  }

  public componentWillUnmount() {
    clearTimeout(this.focusTimeoutId);
  }

  componentDidUpdate({ query, placeholderText }) {
    if (query !== this.props.query || placeholderText !== this.props.placeholderText) {
      this.updateInputWidth();
    }
  }

  copyInputStyles() {
    const inputStyle = window.getComputedStyle(this.input.current);

    STYLE_PROPS.forEach((prop) => {
      this.sizer.current.style[prop] = inputStyle[prop];
    });
  }

  updateInputWidth() {
    let inputWidth;

    if (this.props.autoresize) {
      // scrollWidth is designed to be fast not accurate.
      // +2 is completely arbitrary but does the job.
      inputWidth = Math.ceil(this.sizer.current.scrollWidth) + 2;
    }

    if (inputWidth !== this.state.inputWidth) {
      this.setState({ inputWidth });
    }
  }

  render() {
    const {
      id,
      query,
      ariaLabelText,
      placeholderText,
      expanded,
      classNames,
      isFocused,
      inputAttributes,
      inputEventHandlers,
      index,
      inputType,
      rightInnerComponent,
    } = this.props;

    return (
      <div className={classNames.searchWrapper}>
        {/* eslint-disable-next-line jsx-a11y/role-supports-aria-props */}
        <input
          {...inputAttributes}
          {...inputEventHandlers}
          ref={this.input}
          value={query}
          placeholder={placeholderText}
          className={classNames.searchInput}
          autoFocus={isFocused}
          role='combobox'
          type={inputType || 'text'}
          aria-controls=''
          aria-selected={false}
          aria-autocomplete='list'
          aria-label={ariaLabelText || placeholderText}
          aria-owns={id}
          aria-activedescendant={index > -1 ? `${id}-${index}` : null}
          aria-expanded={expanded}
          style={{ width: this.state.inputWidth }}
        />
        <div ref={this.sizer} style={SIZER_STYLES as CSSProperties}>
          {query || placeholderText}
        </div>
        {rightInnerComponent ? <div className={classNames.rightInnerComponent}>{rightInnerComponent}</div> : null}
      </div>
    );
  }
}

export default Input;
